import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Layout from '../../../layout';
import * as sidebarActions from '../../../actions/sidebar';
import { Tabs, TabList, Tab, TabPill } from '../../../components/common/Tabs';
import Table from '../../../components/common/Table';
import More, { MoreMenu, MoreMenuItem } from '../../../components/common/More';
import * as modalActions from '../../../actions/modals';
import * as propertyActions from '../../../actions/properties';
import * as pressActions from '../../../actions/presses';
import Paginate from '../../../components/Paginate';
import { fetchHandlerFunc } from '../../../utils/fetch-helpers';
import { onDrop } from '../../../utils/sort-helpers';

const propTypes = {
  updateSidebar: PropTypes.func.isRequired,
  fetchProperty: PropTypes.func.isRequired,
  fetchPresses: PropTypes.func.isRequired,
  countPresses: PropTypes.func.isRequired,
  sortPresses: PropTypes.func.isRequired,
  movePress: PropTypes.func.isRequired,
  updatePress: PropTypes.func.isRequired,
  showModal: PropTypes.func.isRequired,
  restaurant: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  presses: PropTypes.array.isRequired,
  match: PropTypes.object,
  meta: PropTypes.object
};

const defaultProps = {
  restaurant: {},
  match: {},
  meta: {}
};

const condfn = val => {
  const cond = { order_by: 'sort_index:ASC' };
  if (val) {
    cond.title = `i%%${val}`;
  }

  return cond;
};

const Press = ({
  updateSidebar,
  fetchProperty,
  fetchPresses,
  countPresses,
  match: {
    url,
    params: { restaurantId }
  },
  updatePress,
  showModal,
  restaurant,
  presses,
  meta,
  isLoading,
  movePress,
  sortPresses
}) => {
  const [refresh, setRefresh] = useState(0);
  const [activeCount, setActivCount] = useState(0);
  const [archivedCount, setArchivedCount] = useState(0);
  const [selected, setSelected] = useState(0);

  const tabsconf = [
    {
      qry: { archived: false },
      cb: setActivCount
    },
    {
      qry: { archived: true },
      cb: setArchivedCount
    },
  ];

  const fetchfn = fetchHandlerFunc(
    fetchPresses,
    countPresses,
    tabsconf,
    restaurantId
  );

  useEffect(() => {
    fetchProperty(restaurantId).then(item => {
      updateSidebar({ titles: { Restaurant: item.name } });
    });

    return () => {};
  }, []);

  useEffect(() => {
    fetchfn(selected, condfn());
    return () => {};
  }, [selected, refresh]);

  const columns = [
    {
      id: 'date',
      Header: 'Date',
      // eslint-disable-next-line react/prop-types
      accessor: props => moment(props.publishDate).format('YYYY-MM-DD'),
      width: 120
    },
    {
      id: 'title',
      Header: 'Press Title',
      accessor: ({ id, title }) => <Link to={`${url}/${id}/edit`}>{title}</Link>
    },
    {
      id: 'publisher',
      Header: 'Publisher',
      // eslint-disable-next-line react/prop-types
      accessor: props => props.publisher
    },
    {
      id: 'displayOnHomepage',
      Header: () => (
        <div className="whitespace-normal break-word">Displays on Home</div>
      ),
      accessor: data => (data.displayOnHomepage ? 'Yes' : 'No'),
      width: 120
    },
    {
      id: 'status',
      Header: 'Status',
      accessor: data => (
        <span className="capitalize">
          {data.published ? 'Published' : 'Unpublished'}
        </span>
      ),
      width: 120
    },
    {
      id: 'more',
      Header: () => <span />,
      accessor: data => (
        <More id={`${data.id}-more`}>
          <MoreMenu id={`${data.id}-moremenu`}>
            <MoreMenuItem
              id={`${data.id}-archive`}
              onClick={() =>
                updatePress(restaurantId, data.id, {
                  archived: !data.archived
                }).then(setRefresh(refresh + 1))}
            >
              {data.archived ? 'Unarchive' : 'Archive'}
            </MoreMenuItem>
            <MoreMenuItem
              id={`${data.id}-delete`}
              onClick={
                () => showModal(
                  'DeletePressModal',
                  {
                    restaurantId,
                    id: data.id,
                    title: data.title
                  },
                  () => {
                    setRefresh(refresh + 1);
                  }
                )
              }
            >
              Delete
            </MoreMenuItem>
          </MoreMenu>
        </More>
      ),
      width: 40
    },
  ];

  return (
    <Layout metaTitle="Press">
      <div className="w-full max-w-3xl">
        <h1 className="mb-4">Press</h1>
        <div className="mb-8">
          Use this page to create and manage Press for{' '}
          {isLoading ? 'Restaurant' : restaurant.name}.
        </div>
        {isLoading ? (
          <div>Loading ...</div>
        ) : (
          <div>
            <div className="sm:flex sm:flex-1 justify-start mb-6 md:mb-0 md:justify-end">
              <Link
                to={`${url}/new`}
                className="btn no-underline mb-6 sm:mb-0 w-full sm:w-auto"
              >
                + New Press
              </Link>
            </div>
            <Tabs selectedIndex={selected} onSelect={i => setSelected(i)}>
              <TabList>
                <Tab>
                  <span className="mr-2">Active</span>
                  <TabPill id="active-pill">{activeCount}</TabPill>
                </Tab>
                <Tab>
                  <span className="mr-2">Archived</span>
                  <TabPill id="archived-pill">{archivedCount}</TabPill>
                </Tab>
                <Paginate
                  meta={meta}
                  fetchHandler={(_, link) => fetchPresses(null, null, link)}
                />
              </TabList>
              <Table
                id="press-table"
                data={presses}
                columns={columns}
                moveRow={(drag, hover) => movePress(drag, hover)}
                onDrop={(evt, moved) => {
                  if (moved) {
                    onDrop(evt, presses, sortPresses.bind(null, restaurantId), {
                      archived: selected ? true : false // eslint-disable-line no-unneeded-ternary
                    });
                  }
                }}
                isDraggable
              />
            </Tabs>
            <Paginate
              meta={meta}
              fetchHandler={(_, link) => fetchPresses(null, null, link)}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

Press.propTypes = propTypes;
Press.defaultProps = defaultProps;

export default connect(
  (state) => ({
    restaurant: state.property.data,
    presses: state.presses.data,
    meta: state.presses.meta,
    isLoading: state.property.isLoading || state.presses.isLoading
  }),
  {
    ...sidebarActions,
    ...modalActions,
    ...propertyActions,
    ...pressActions
  }
)(Press);
